import { sourcingBanner, type SourcingRun } from "@vidario/domain";
import { Button, Card, cn, Spinner } from "@vidario/ui";
import { Square } from "lucide-react";
import { useState, type ReactElement } from "react";

import { StopSearchDialog } from "./StopSearchDialog";

const TONE_CLASS = {
  progress: {
    card: "border-primary-100 bg-primary-50",
    title: "text-primary-800",
    detail: "text-primary-700",
  },
  stopped: {
    card: "border-border-default bg-neutral-50",
    title: "text-text-primary",
    detail: "text-text-secondary",
  },
  failed: { card: "border-danger-200 bg-danger-50", title: "text-danger-700", detail: "text-danger-600" },
  degraded: {
    card: "border-warning-500 bg-warning-50",
    title: "text-warning-700",
    detail: "text-warning-700",
  },
} as const;

/** Search banner for the Longlist: progress while a run is active (with the
 * admin's „Suche stoppen", #88), a stopped run, a failed or degraded run. A
 * clean finished run shows nothing: its candidates are the result. */
export function SourcingProgress({
  run,
  jobId,
  isAdmin,
}: {
  run: SourcingRun;
  jobId: string;
  isAdmin: boolean;
}): ReactElement | null {
  const [stopOpen, setStopOpen] = useState(false);
  const banner = sourcingBanner(run, { isAdmin });
  if (!banner) return null;
  const tone = TONE_CLASS[banner.tone];

  return (
    <Card
      className={cn("mb-6 flex items-center gap-3.5 px-6 py-4", tone.card)}
      data-testid="sourcing-progress"
    >
      {banner.tone === "progress" && <Spinner size={18} className="text-primary-600" />}
      <div className="min-w-0 flex-1">
        <div className={cn("text-sm font-semibold", tone.title)}>{banner.title}</div>
        <div className={cn("mt-0.5 text-xs", tone.detail)}>
          {banner.detail ?? `${run.foundCount} gefunden · ${run.scoredCount} bewertet`}
        </div>
      </div>
      {banner.canStop && (
        <>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Square size={14} />}
            onClick={() => setStopOpen(true)}
          >
            Suche stoppen
          </Button>
          <StopSearchDialog jobId={jobId} open={stopOpen} onOpenChange={setStopOpen} />
        </>
      )}
    </Card>
  );
}
